import type { Placement } from './types';

export function isElementVisiblyLaidOut(el: HTMLElement | null): boolean {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const sizeOk = r.width > 0 && r.height > 0;
  const visibleOk = style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity || '1') > 0.05;
  return sizeOk && visibleOk;
}

export function computeTooltipPlacement(
  rect: DOMRect,
  tooltipW: number,
  tooltipH: number,
  preferredPlacements: Placement[],
): { placement: Placement; top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 16;

  const topSpace = rect.top;
  const bottomSpace = vh - rect.bottom;
  const leftSpace = rect.left;
  const rightSpace = vw - rect.right;

  const fits = {
    bottom: bottomSpace >= tooltipH + margin + 8,
    top: topSpace >= tooltipH + margin + 8,
    right: rightSpace >= tooltipW + margin + 8,
    left: leftSpace >= tooltipW + margin + 8,
  } as Record<Placement, boolean>;

  const fitting = preferredPlacements.find(p => fits[p]);
  const placement: Placement = fitting || (['bottom','top','right','left'] as Placement[])
    .sort((a, b) => {
      const spaces: Record<Placement, number> = { bottom: bottomSpace, top: topSpace, right: rightSpace, left: leftSpace };
      return spaces[b] - spaces[a];
    })[0];

  let top = 0;
  let left = 0;
  if (placement === 'bottom') {
    top = rect.bottom + margin;
    left = rect.left + (rect.width / 2) - (tooltipW / 2);
    const minLeft = 8;
    const maxLeft = vw - tooltipW - 8;
    left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
  } else if (placement === 'top') {
    top = rect.top - tooltipH - margin;
    left = rect.left + (rect.width / 2) - (tooltipW / 2);
    const minLeft = 8;
    const maxLeft = vw - tooltipW - 8;
    left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
  } else if (placement === 'right') {
    top = rect.top + (rect.height / 2) - (tooltipH / 2);
    left = rect.right + margin;
    const minTop = 8;
    const maxTop = vh - tooltipH - 8;
    top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop));
  } else {
    top = rect.top + (rect.height / 2) - (tooltipH / 2);
    left = rect.left - tooltipW - margin;
    const minTop = 8;
    const maxTop = vh - tooltipH - 8;
    top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop));
  }

  return { placement, top, left };
} 